import { useEffect } from "react";
import { useQuery } from "react-query";
import { useLoadScript } from "@react-google-maps/api";
import { useFormik } from "formik";
import * as yup from "yup";
import styled from "styled-components";
import { toast } from "react-toastify";

import {
	TextField,
	Typography,
	Grid,
	Box,
	Button,
	CircularProgress,
} from "../../components/common/muiComponents";
import Loader from "../../components/common/SpinLoader";
import Map from "../../components/ContactUs/Map";
import { submitContactUs, getUserIP } from "../../api/common/request";
import config from "../../config";

const { googleMapsApiKey } = config().secrets;

const libraries = ["places"];

const mainTextStyle = {
	textAlign: "center",
	marginBottom: { md: "2rem", xs: "1rem" },
	textTransform: "capitalize",
};

const gridContainerStyle = {
	boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
	borderRadius: "1.5rem",
	padding: {
		xs: "1rem",
		md: "2rem",
	},
	marginBottom: {
		xs: "1rem",
		md: "2rem",
	},
};

const buttonStyle = {
	textTransform: "capitalize",
	pt: ".8rem",
	pb: ".8rem",
	mt: "1.5rem",
	fontSize: "1rem",
};

const validationSchema = yup.object({
	name: yup
		.string("Please enter your name")
		.min(3, "Name should be of minimum 3 characters length")
		.required("Name is required"),
	email: yup
		.string("Please enter your email")
		.email("Please enter a valid email")
		.required("Email is required"),
	phone: yup
		.number("Please enter your password")
		.min(11, "Password should be of minimum 11 characters length")
		.required("Password is required"),
	address: yup
		.string("Please enter your address")
		.min(3, "Address should be of minimum 3 characters length")
		.required("Address is required"),
	subject: yup
		.string("Please enter your subject")
		.min(3, "Subject should be of minimum 3 characters length")
		.required("Subject is required"),
	message: yup
		.string("Please enter your message")
		.min(3, "Message should be of minimum 3 characters length")
		.max(500, "Message should be of maximum 500 characters length")
		.required("Message is required"),
});

const initialValues = {
	name: "",
	email: "",
	phone: "",
	address: "",
	subject: "",
	message: "",
};

const ContactUs = () => {
	const { isLoaded } = useLoadScript({
		googleMapsApiKey,
		libraries,
	});

	const {
		data: userIP = "",
		isError: iPError,
		error: iPErrorMessage,
	} = useQuery("get-user-ip", async () => getUserIP(), {
		staleTime: Infinity,
		select: (data) => data?.ip,
	});

	useEffect(() => {
		if (iPError) {
			if (iPErrorMessage.response) {
				toast.error(iPErrorMessage.response.data?.message);
			} else if (iPErrorMessage.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", iPErrorMessage.message);
			}
		}
	}, [iPError, iPErrorMessage]);

	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit: (values) => {
			handleSubmit(
				values,
				() => formik.setSubmitting(false),
				() => formik.resetForm()
			);
		},
	});

	const handleSubmit = async (values, stopSubmitLoading, resetForm) => {
		try {
			const payload = {
				fullName: values.name,
				emailAddress: values.email,
				phone: values.phone,
				address: values.address,
				subject: values.subject,
				message: values.message,
				ipAddress: userIP,
			};
			const result = await submitContactUs(payload);
			stopSubmitLoading();
			resetForm();
			toast.success(result.message);
		} catch (error) {
			stopSubmitLoading();
			if (error) {
				if (error.response) {
					toast.error(error.response.data?.message);
				} else if (error.request) {
					toast.error("Internal Server Error");
				} else {
					toast.error("Error", error.message);
				}
			}
		}
	};

	return (
		<Container>
			<Grid container sx={gridContainerStyle}>
				<Grid item xs={12} md={6}>
					<Typography variant="h3" component="h2" sx={mainTextStyle}>
						contact us
					</Typography>
					<Typography variant="body1" component="p" sx={mainTextStyle}>
						Plot 2b, Fatai Arobieke Street. off Admiralty Road. Lekki. Lagos
					</Typography>
					<Typography variant="body1" component="p" sx={mainTextStyle}>
						info@makvid.com.ng
					</Typography>
				</Grid>
				<Grid
					item
					xs={12}
					md={6}
					component="form"
					noValidate
					autoComplete="off"
					onSubmit={formik.handleSubmit}
				>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<TextField
								name="name"
								label="Name"
								variant="outlined"
								fullWidth
								value={formik.values.name}
								onChange={formik.handleChange}
								error={formik.touched.name && Boolean(formik.errors.name)}
								helperText={formik.touched.name && formik.errors.name}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								name="email"
								label="Email"
								variant="outlined"
								fullWidth
								value={formik.values.email}
								onChange={formik.handleChange}
								error={formik.touched.email && Boolean(formik.errors.email)}
								helperText={formik.touched.email && formik.errors.email}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								name="phone"
								label="Phone"
								variant="outlined"
								fullWidth
								value={formik.values.phone}
								onChange={formik.handleChange}
								error={formik.touched.phone && Boolean(formik.errors.phone)}
								helperText={formik.touched.phone && formik.errors.phone}
								type="number"
								inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								name="address"
								label="Address"
								variant="outlined"
								fullWidth
								value={formik.values.address}
								onChange={formik.handleChange}
								error={formik.touched.address && Boolean(formik.errors.address)}
								helperText={formik.touched.address && formik.errors.address}
							/>
						</Grid>
					</Grid>

					<Box sx={{ m: "1rem 0" }}>
						<TextField
							name="subject"
							label="Subject"
							variant="outlined"
							fullWidth
							value={formik.values.subject}
							onChange={formik.handleChange}
							error={formik.touched.subject && Boolean(formik.errors.subject)}
							helperText={formik.touched.subject && formik.errors.subject}
						/>
					</Box>

					<Box sx={{ m: "1rem 0" }}>
						<TextField
							name="message"
							label="Type your message here..."
							variant="outlined"
							fullWidth
							value={formik.values.message}
							onChange={formik.handleChange}
							error={formik.touched.message && Boolean(formik.errors.message)}
							helperText={formik.touched.message && formik.errors.message}
							multiline
							rows={5}
						/>
					</Box>

					<Box>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							sx={buttonStyle}
							fullWidth
							disabled={formik.isSubmitting}
						>
							{formik.isSubmitting ? (
								<CircularProgress size="1.5rem" />
							) : (
								"submit"
							)}
						</Button>
					</Box>
				</Grid>
			</Grid>

			<Box sx={{ borderRadius: "1rem" }}>
				{!isLoaded && <Loader />}
				{isLoaded && <Map />}
			</Box>
		</Container>
	);
};

export default ContactUs;

const Container = styled.div`
	padding: 2rem 4rem;
    padding-top: 9rem;
    
	@media (max-width: 768px) {
        padding: 1rem;
        padding-top: 6rem;
	}
`;
